import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import TextSnippetRoundedIcon from '@mui/icons-material/TextSnippetRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import RotateRightRoundedIcon from '@mui/icons-material/RotateRightRounded';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.snow.css';
import learningMaterialService, {
  LearningMaterial,
  LearningMaterialSection,
  CreateLearningMaterialDto,
  MaterialMediaAttachmentInput,
  Media,
} from '../../services/learningMaterialService';
import AuthoringAssistantPanel from '../AI/AuthoringAssistantPanel';
import { AuthoringAssistantGeneratedDraftResponse } from '../../services/authoringAssistantService';
import LearningMaterialContent from './LearningMaterialContent';

const QuillEditor = ReactQuill as any;

const placementOptions: Array<{
  value: NonNullable<MaterialMediaAttachmentInput['placement']>;
  label: string;
}> = [
  { value: 'gallery', label: 'Galleria osion jälkeen' },
  { value: 'top', label: 'Osion yläpuolella' },
  { value: 'bottom', label: 'Osion alapuolella' },
  { value: 'left', label: 'Osion vasemmalla puolella' },
  { value: 'right', label: 'Osion oikealla puolella' },
];

const editorFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'code-block',
  'list',
  'bullet',
  'color',
  'background',
  'link',
  'image',
  'video',
];

const editorToolbar = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ color: [] }, { background: [] }],
  ['link', 'image', 'video'],
  ['clean'],
];

const createSectionLocalId = () => `section-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeRotation = (rotation?: number) => {
  if (!Number.isFinite(rotation)) {
    return 0;
  }

  const normalizedQuarterTurns = Math.round((rotation as number) / 90);
  return (((normalizedQuarterTurns % 4) + 4) % 4) * 90;
};

interface EditableMaterialSection {
  localId: string;
  id?: number;
  title: string;
  content: string;
  mediaAttachments: Array<MaterialMediaAttachmentInput & { media: Media }>;
}

interface LearningMaterialFormProps {
  lessonId: number;
  material?: LearningMaterial;
  onSave: () => void;
  onCancel: () => void;
  assistantInitialPrompt?: string;
  assistantAutoGenerate?: boolean;
}

const mapSectionMedia = (
  attachments: NonNullable<LearningMaterialSection['sectionMedia']>,
): Array<MaterialMediaAttachmentInput & { media: Media }> => attachments
  .filter((attachment) => attachment?.media?.fileType === 'image')
  .map((attachment) => ({
    mediaId: attachment.mediaId,
    order: attachment.order,
    placement: (attachment.placement as MaterialMediaAttachmentInput['placement']) || 'gallery',
    caption: attachment.caption,
    width: attachment.width,
    rotation: normalizeRotation(attachment.rotation),
    media: attachment.media,
  }));

const createEmptySection = (): EditableMaterialSection => ({
  localId: createSectionLocalId(),
  title: '',
  content: '',
  mediaAttachments: [],
});

const sectionHasVisibleContent = (section: EditableMaterialSection) => (
  Boolean(section.title.trim())
  || Boolean(section.content.trim() && section.content.trim() !== '<p><br></p>')
  || section.mediaAttachments.length > 0
);

const buildSectionsFromMaterial = (material?: LearningMaterial): EditableMaterialSection[] => {
  if (material?.sections && material.sections.length > 0) {
    return material.sections
      .slice()
      .sort((left, right) => (left.order || 0) - (right.order || 0))
      .map((section) => ({
        localId: createSectionLocalId(),
        id: section.id,
        title: section.title || '',
        content: section.content || '',
        mediaAttachments: mapSectionMedia(section.sectionMedia || []),
      }));
  }

  if (material?.content || (material?.materialMedia && material.materialMedia.length > 0)) {
    return [{
      localId: createSectionLocalId(),
      title: '',
      content: material?.content || '',
      mediaAttachments: (material?.materialMedia || [])
        .filter((attachment) => attachment?.media?.fileType === 'image')
        .map((attachment) => ({
          mediaId: attachment.mediaId,
          order: attachment.order,
          placement: (attachment.placement as MaterialMediaAttachmentInput['placement']) || 'gallery',
          caption: attachment.caption,
          width: attachment.width,
          rotation: normalizeRotation(attachment.rotation),
          media: attachment.media,
        })),
    }];
  }

  return [createEmptySection()];
};

const LearningMaterialForm: React.FC<LearningMaterialFormProps> = ({
  lessonId,
  material,
  onSave,
  onCancel,
  assistantInitialPrompt,
  assistantAutoGenerate = false,
}) => {
  const quillRefs = useRef<Record<string, any>>({});
  const extractCameraInputRef = useRef<HTMLInputElement | null>(null);
  const extractFileInputRef = useRef<HTMLInputElement | null>(null);
  const separateCameraInputRef = useRef<HTMLInputElement | null>(null);
  const separateFileInputRef = useRef<HTMLInputElement | null>(null);
  const extractTargetSectionIdRef = useRef<string | null>(null);
  const separateImageTargetSectionIdRef = useRef<string | null>(null);

  const [title, setTitle] = useState(material?.title || '');
  const [description, setDescription] = useState(material?.description || '');
  const [isPublic, setIsPublic] = useState(material?.isPublic || false);
  const [sections, setSections] = useState<EditableMaterialSection[]>(() => buildSectionsFromMaterial(material));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExtractingText, setIsExtractingText] = useState(false);
  const [isUploadingSeparateImage, setIsUploadingSeparateImage] = useState(false);

  useEffect(() => {
    if (material) {
      setTitle(material.title);
      setDescription(material.description || '');
      setIsPublic(material.isPublic);
      setSections(buildSectionsFromMaterial(material));
      return;
    }

    setSections([createEmptySection()]);
  }, [material]);

  const escapeHtml = (value: string) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const plainTextToHtml = (value: string) => value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`)
    .join('');

  const normalizeExtractedContent = (value: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return '';
    }

    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(trimmedValue);
    return looksLikeHtml ? trimmedValue : plainTextToHtml(trimmedValue);
  };

  const normalizeSectionContent = (value: string) => {
    const trimmedValue = value.trim();
    return trimmedValue === '<p><br></p>' ? '' : trimmedValue;
  };

  const hasAnySectionContent = useMemo(
    () => sections.some((section) => sectionHasVisibleContent(section)),
    [sections],
  );

  const composedMaterialContent = useMemo(() => sections
    .map((section) => {
      const parts = [
        section.title.trim() ? `<h2>${escapeHtml(section.title.trim())}</h2>` : '',
        normalizeSectionContent(section.content),
      ].filter(Boolean);

      return parts.join('');
    })
    .filter(Boolean)
    .join('<p><br></p>'), [sections]);

  const updateSection = useCallback((localId: string, updater: (section: EditableMaterialSection) => EditableMaterialSection) => {
    setSections((previousSections) => previousSections.map((section) => (
      section.localId === localId ? updater(section) : section
    )));
  }, []);

  const handleInlineImageUpload = useCallback(async (sectionLocalId: string) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('capture', 'environment');
    input.click();

    input.onchange = async () => {
      if (!input.files || input.files.length === 0) {
        return;
      }

      try {
        const media = await learningMaterialService.uploadMedia(input.files[0]);
        const quillEditor = quillRefs.current[sectionLocalId]?.getEditor?.();

        if (quillEditor) {
          const range = quillEditor.getSelection(true);
          const index = range && typeof range.index === 'number'
            ? range.index
            : quillEditor.getLength();
          quillEditor.insertEmbed(index, 'image', media.blobUrl);
          quillEditor.setSelection(index + 1);
        }
      } catch (err) {
        console.error('Failed to upload inline image:', err);
        setError('Kuvan lataus epäonnistui');
      }
    };
  }, []);

  const createQuillModules = useCallback((sectionLocalId: string) => ({
    toolbar: {
      container: editorToolbar,
      handlers: {
        image: () => {
          void handleInlineImageUpload(sectionLocalId);
        },
      },
    },
  }), [handleInlineImageUpload]);

  const handleSectionContentChange = useCallback((localId: string, value: string) => {
    updateSection(localId, (section) => ({
      ...section,
      content: value,
    }));
  }, [updateSection]);

  const handleSectionTitleChange = useCallback((localId: string, value: string) => {
    updateSection(localId, (section) => ({
      ...section,
      title: value,
    }));
  }, [updateSection]);

  const openExtractInput = (localId: string, mode: 'camera' | 'file') => {
    extractTargetSectionIdRef.current = localId;
    if (mode === 'camera') {
      extractCameraInputRef.current?.click();
      return;
    }

    extractFileInputRef.current?.click();
  };

  const openSeparateImageInput = (localId: string, mode: 'camera' | 'file') => {
    separateImageTargetSectionIdRef.current = localId;
    if (mode === 'camera') {
      separateCameraInputRef.current?.click();
      return;
    }

    separateFileInputRef.current?.click();
  };

  const handleExtractTextFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const targetSectionId = extractTargetSectionIdRef.current;
    event.target.value = '';
    extractTargetSectionIdRef.current = null;

    if (!file || !targetSectionId) {
      return;
    }

    setIsExtractingText(true);
    setError(null);

    try {
      const extractedText = await learningMaterialService.extractTextFromImage(file);
      const extractedHtml = normalizeExtractedContent(extractedText);
      updateSection(targetSectionId, (section) => {
        const normalizedContent = normalizeSectionContent(section.content);
        return {
          ...section,
          content: `${normalizedContent}${normalizedContent ? '<p><br></p>' : ''}${extractedHtml}`,
        };
      });
    } catch (err: any) {
      console.error('Failed to extract text from image:', err);
      setError(err.response?.data?.message || 'Kuvan tekstintunnistus epäonnistui');
    } finally {
      setIsExtractingText(false);
    }
  };

  const handleSeparateImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const targetSectionId = separateImageTargetSectionIdRef.current;
    event.target.value = '';
    separateImageTargetSectionIdRef.current = null;

    if (!file || !targetSectionId) {
      return;
    }

    setIsUploadingSeparateImage(true);
    setError(null);

    try {
      const media = await learningMaterialService.uploadMedia(file);
      updateSection(targetSectionId, (section) => ({
        ...section,
        mediaAttachments: [
          ...section.mediaAttachments,
          {
            mediaId: media.id,
            order: section.mediaAttachments.length + 1,
            placement: 'gallery',
            width: undefined,
            caption: '',
            rotation: 0,
            media,
          },
        ],
      }));
    } catch (err: any) {
      console.error('Failed to upload separate material image:', err);
      setError(err.response?.data?.message || 'Erillisen kuvan lataus epäonnistui');
    } finally {
      setIsUploadingSeparateImage(false);
    }
  };

  const handleAddSection = () => {
    setSections((previousSections) => [...previousSections, createEmptySection()]);
  };

  const handleRemoveSection = (localId: string) => {
    setSections((previousSections) => {
      const nextSections = previousSections.filter((section) => section.localId !== localId);
      return nextSections.length > 0 ? nextSections : [createEmptySection()];
    });
  };

  const handleMoveSection = (localId: string, direction: 'up' | 'down') => {
    setSections((previousSections) => {
      const currentIndex = previousSections.findIndex((section) => section.localId === localId);

      if (currentIndex === -1) {
        return previousSections;
      }

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= previousSections.length) {
        return previousSections;
      }

      const nextSections = [...previousSections];
      const [movedSection] = nextSections.splice(currentIndex, 1);
      nextSections.splice(targetIndex, 0, movedSection);
      return nextSections;
    });
  };

  const handleRemoveSectionImage = (localId: string, mediaId: number) => {
    updateSection(localId, (section) => ({
      ...section,
      mediaAttachments: section.mediaAttachments
        .filter((attachment) => attachment.mediaId !== mediaId)
        .map((attachment, index) => ({
          ...attachment,
          order: index + 1,
        })),
    }));
  };

  const handleSectionImageCaptionChange = (localId: string, mediaId: number, caption: string) => {
    updateSection(localId, (section) => ({
      ...section,
      mediaAttachments: section.mediaAttachments.map((attachment) => (
        attachment.mediaId === mediaId
          ? { ...attachment, caption }
          : attachment
      )),
    }));
  };

  const handleRotateSectionImage = (localId: string, mediaId: number, direction: 'left' | 'right') => {
    updateSection(localId, (section) => ({
      ...section,
      mediaAttachments: section.mediaAttachments.map((attachment) => {
        if (attachment.mediaId !== mediaId) {
          return attachment;
        }

        const delta = direction === 'left' ? -90 : 90;
        return {
          ...attachment,
          rotation: normalizeRotation((attachment.rotation || 0) + delta),
        };
      }),
    }));
  };

  const handleSectionImagePlacementChange = (
    localId: string,
    mediaId: number,
    placement: NonNullable<MaterialMediaAttachmentInput['placement']>,
  ) => {
    updateSection(localId, (section) => ({
      ...section,
      mediaAttachments: section.mediaAttachments.map((attachment) => (
        attachment.mediaId === mediaId
          ? {
              ...attachment,
              placement,
              width: placement === 'left' || placement === 'right'
                ? attachment.width || '280px'
                : attachment.width === '280px'
                  ? undefined
                  : attachment.width,
            }
          : attachment
      )),
    }));
  };

  const handleMoveSectionImage = (localId: string, mediaId: number, direction: 'up' | 'down') => {
    updateSection(localId, (section) => {
      const currentIndex = section.mediaAttachments.findIndex((attachment) => attachment.mediaId === mediaId);

      if (currentIndex === -1) {
        return section;
      }

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= section.mediaAttachments.length) {
        return section;
      }

      const nextAttachments = [...section.mediaAttachments];
      const [movedAttachment] = nextAttachments.splice(currentIndex, 1);
      nextAttachments.splice(targetIndex, 0, movedAttachment);

      return {
        ...section,
        mediaAttachments: nextAttachments.map((attachment, index) => ({
          ...attachment,
          order: index + 1,
        })),
      };
    });
  };

  const buildSectionPayload = () => sections
    .map((section, index) => ({
      title: section.title.trim() || undefined,
      content: normalizeSectionContent(section.content) || undefined,
      order: index + 1,
      mediaAttachments: section.mediaAttachments.map((attachment, attachmentIndex) => ({
        mediaId: attachment.mediaId,
        order: attachmentIndex + 1,
        placement: attachment.placement || 'gallery',
        caption: attachment.caption || undefined,
        width: attachment.width || undefined,
        rotation: normalizeRotation(attachment.rotation),
      })),
    }))
    .filter((section) => Boolean(section.title || section.content || section.mediaAttachments.length));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data: CreateLearningMaterialDto = {
        title,
        description: description || undefined,
        lessonId,
        isPublic,
        content: composedMaterialContent,
        sections: buildSectionPayload(),
      };

      if (material) {
        await learningMaterialService.updateMaterial(material.id, data);
      } else {
        await learningMaterialService.createMaterial(data);
      }

      onSave();
    } catch (err: any) {
      console.error('Failed to save material:', err);
      setError(err.response?.data?.message || 'Materiaalin tallennus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDraft = (draft: AuthoringAssistantGeneratedDraftResponse) => {
    if (!draft.material) {
      return;
    }

    setTitle(draft.material.title || '');
    setDescription(draft.material.description || '');
    setSections([
      {
        localId: createSectionLocalId(),
        title: '',
        content: draft.material.content || '',
        mediaAttachments: [],
      },
    ]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 3,
        flexDirection: { xs: 'column', xl: 'row' },
      }}
    >
      <Paper elevation={2} sx={{ p: 3, flex: 1, minWidth: 0, width: '100%' }}>
        <Typography variant="h6" gutterBottom>
          {material ? 'Muokkaa materiaalia' : 'Luo uusi materiaali'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Otsikko"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Kuvaus (valinnainen)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Kirjoita materiaali suoraan editoriin tai tuo sisältö kuvasta. Materiaaliosiota voidaan näyttää opiskelijalle osio kerrallaan tai kokonaisena.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Jokaisella osiolla voi olla oma teksti ja omat kuvat.
            </Typography>
          </Box>

          <input
            ref={extractCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleExtractTextFileChange}
          />
          <input
            ref={extractFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleExtractTextFileChange}
          />
          <input
            ref={separateCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleSeparateImageFileChange}
          />
          <input
            ref={separateFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleSeparateImageFileChange}
          />

          <Stack spacing={2} divider={<Divider flexItem />} sx={{ mb: 2 }}>
            {sections.map((section, index) => (
              <Box key={section.localId}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1">
                    Osio {index + 1}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveSection(section.localId, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveSection(section.localId, 'down')}
                      disabled={index === sections.length - 1}
                    >
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleRemoveSection(section.localId)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Osion otsikko (valinnainen)"
                  value={section.title}
                  onChange={(event) => handleSectionTitleChange(section.localId, event.target.value)}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCameraRoundedIcon />}
                    onClick={() => openExtractInput(section.localId, 'camera')}
                    disabled={loading || isExtractingText}
                  >
                    {isExtractingText ? 'Tunnistetaan tekstiä...' : 'Ota kuva ja tunnista teksti'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TextSnippetRoundedIcon />}
                    onClick={() => openExtractInput(section.localId, 'file')}
                    disabled={loading || isExtractingText}
                  >
                    Valitse kuva tiedostosta
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PhotoCameraRoundedIcon />}
                    onClick={() => openSeparateImageInput(section.localId, 'camera')}
                    disabled={loading || isUploadingSeparateImage}
                  >
                    {isUploadingSeparateImage ? 'Ladataan kuvaa...' : 'Ota osion kuva'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ImageRoundedIcon />}
                    onClick={() => openSeparateImageInput(section.localId, 'file')}
                    disabled={loading || isUploadingSeparateImage}
                  >
                    Lisää osion kuva tiedostosta
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Kamera on ensisijainen tapa lisätä kuva. Voit tunnistaa siitä tekstin tähän osioon tai liittää kuvan osion yhteyteen erilliseksi kuvaksi.
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Kirjoita osion teksti käsin
                </Typography>
                <Box sx={{ backgroundColor: 'white', minHeight: '350px', mb: 2 }}>
                  <QuillEditor
                    ref={(value: any) => {
                      if (value) {
                        quillRefs.current[section.localId] = value;
                        return;
                      }

                      delete quillRefs.current[section.localId];
                    }}
                    theme="snow"
                    value={section.content}
                    onChange={(value: string) => handleSectionContentChange(section.localId, value)}
                    modules={createQuillModules(section.localId)}
                    formats={editorFormats}
                    style={{ height: '300px' }}
                  />
                </Box>

                <Box sx={{ mt: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Osion kuvat
                  </Typography>
                  {section.mediaAttachments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Ei vielä kuvia tässä osiossa.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      {section.mediaAttachments.map((attachment) => (
                        <Paper key={`${section.localId}-${attachment.mediaId}`} variant="outlined" sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                component="img"
                                src={attachment.media.blobUrl}
                                alt={attachment.media.altText || attachment.caption || attachment.media.originalFileName}
                                sx={{ width: '100%', maxWidth: 320, borderRadius: 1, display: 'block', mb: 1 }}
                              />
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                {attachment.media.originalFileName}
                              </Typography>
                              <Box sx={{ display: 'grid', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Järjestys: {attachment.order || 1}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMoveSectionImage(section.localId, attachment.mediaId, 'up')}
                                    disabled={(attachment.order || 1) === 1}
                                  >
                                    <ArrowUpwardRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMoveSectionImage(section.localId, attachment.mediaId, 'down')}
                                    disabled={(attachment.order || 1) === section.mediaAttachments.length}
                                  >
                                    <ArrowDownwardRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRotateSectionImage(section.localId, attachment.mediaId, 'left')}
                                  >
                                    <RotateLeftRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRotateSectionImage(section.localId, attachment.mediaId, 'right')}
                                  >
                                    <RotateRightRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <Typography variant="body2" color="text.secondary">
                                    Kierto: {normalizeRotation(attachment.rotation)}°
                                  </Typography>
                                </Box>

                                <FormControl fullWidth>
                                  <InputLabel id={`section-image-placement-${section.localId}-${attachment.mediaId}`}>
                                    Sijoittelu
                                  </InputLabel>
                                  <Select
                                    labelId={`section-image-placement-${section.localId}-${attachment.mediaId}`}
                                    value={attachment.placement || 'gallery'}
                                    label="Sijoittelu"
                                    onChange={(event) => handleSectionImagePlacementChange(
                                      section.localId,
                                      attachment.mediaId,
                                      event.target.value as NonNullable<MaterialMediaAttachmentInput['placement']>,
                                    )}
                                  >
                                    {placementOptions.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                <TextField
                                  fullWidth
                                  label="Kuvateksti"
                                  value={attachment.caption || ''}
                                  onChange={(event) => handleSectionImageCaptionChange(section.localId, attachment.mediaId, event.target.value)}
                                />
                              </Box>
                            </Box>
                            <IconButton color="error" onClick={() => handleRemoveSectionImage(section.localId, attachment.mediaId)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>

                {sectionHasVisibleContent(section) && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Osion esikatselu
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <LearningMaterialContent
                        material={{
                          content: section.content,
                          sections: [{
                            id: section.id || index + 1,
                            title: section.title || null,
                            content: section.content,
                            order: 1,
                            sectionMedia: section.mediaAttachments.map((attachment, attachmentIndex) => ({
                              mediaId: attachment.mediaId,
                              order: attachmentIndex + 1,
                              placement: attachment.placement || 'gallery',
                              caption: attachment.caption,
                              width: attachment.width,
                                rotation: normalizeRotation(attachment.rotation),
                              media: attachment.media,
                            })),
                          }],
                        }}
                      />
                    </Paper>
                  </Box>
                )}
              </Box>
            ))}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<AddCircleOutlineRoundedIcon />} onClick={handleAddSection}>
              Lisää tekstiosio käsin
            </Button>
          </Box>

          {hasAnySectionContent && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Esikatselu
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <LearningMaterialContent
                  material={{
                    content: composedMaterialContent,
                    sections: sections.map((section, index) => ({
                      id: section.id || index + 1,
                      title: section.title || null,
                      content: section.content,
                      order: index + 1,
                      sectionMedia: section.mediaAttachments.map((attachment, attachmentIndex) => ({
                        mediaId: attachment.mediaId,
                        order: attachmentIndex + 1,
                        placement: attachment.placement || 'gallery',
                        caption: attachment.caption,
                        width: attachment.width,
                        rotation: normalizeRotation(attachment.rotation),
                        media: attachment.media,
                      })),
                    })),
                  }}
                />
              </Paper>
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
            }
            label="Tee tämä materiaali julkiseksi (näkyvissä kaikille opiskelijoille)"
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel} disabled={loading}>
              Peruuta
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !title || !hasAnySectionContent}
            >
              {loading ? 'Tallennetaan...' : material ? 'Päivitä' : 'Luo'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <AuthoringAssistantPanel
        lessonId={lessonId}
        materialId={material?.id}
        draftMaterial={{
          title,
          description,
          content: composedMaterialContent,
        }}
        draftTarget="material"
        onApplyDraft={handleApplyDraft}
        initialPrompt={assistantInitialPrompt}
        autoGenerateInitialDraft={assistantAutoGenerate}
      />
    </Box>
  );
};

export default LearningMaterialForm;
